import { Injectable } from '@angular/core';
import { FinancialClient } from '@gipi-financial/client/models/client.model';
import { FinancialCompany } from '@gipi-financial/company/models/company.model';
import { FinancialCompanyService } from '@gipi-financial/company/services/company.service';
import { FinancialPerson } from '@gipi-financial/person/models/person.model';
import { OAuthUser } from '@gipi-financial/user/models/user.model';
import { StateAcronym } from '@gipi-shared/enums/state-acronym.enum';
import { CustomAuthenticationService } from '@gipi-shared/services/custom-authentication.service';
import { AcbrIEUtil } from '@gipi-shared/utils/acbr-ie.util';
import { ArrayUtil, CurrencyUtil, DateUtil, GIPIAbstractService, ObjectUtil, PhoneUtil, StringUtil, TokenDTO } from '@gipisistemas/ng-core';
import jsPDF, { TextOptionsLight } from 'jspdf';
import * as moment from 'moment';
import { FinancialCreditClientType } from '../enums/credit-client-type.enum';

interface PersonData {
    name: string,
    cpfOrCnpj: string,
    stateRegistration: string,
    street: string,
    city: string,
    zipCode: string,
    neighborhood: string,
    phones: string,
}

export interface PdfCreditClientReceiptModel {
    showTwoWays: boolean;
    creditClientClient: FinancialClient;
    creditClientAmount: number;
    creditClientDescription: string;
    creditClientDate: Date;
    creditClientType: FinancialCreditClientType;
    creditCanceled: boolean;
}

@Injectable({ providedIn: 'root' })
export class FinancialPdfCreditClientReceiptService extends GIPIAbstractService {

    private _companyData: PersonData = null;

    constructor(
        private _authenticationService: CustomAuthenticationService,
        private _companyService: FinancialCompanyService,
    ) {
        super('', '');
        this._getCompanyLogged();
    }

    public exportPdf(options: PdfCreditClientReceiptModel): void {
        if (ObjectUtil.isNull(options) ||
            ObjectUtil.isNull(this._companyData)) {
            return;
        }

        const clientData = !ObjectUtil.isNull(options.creditClientClient) ? this._getPersonInfo(options.creditClientClient.person) : null;
        if (ObjectUtil.isNull(clientData)) {
            return;
        }

        const creditClientAmountFormatted: string = CurrencyUtil.transform(options.creditClientAmount, '1.2-2');
        const creditClientAmountInWords = `${creditClientAmountFormatted} (${creditClientAmountFormatted.substring(2).trim().numberFullName(true)})`;

        const creditClientDescription = options.creditClientDescription;
        const creditClientDate = DateUtil.format(options.creditClientDate, DateUtil.DATE_FORMAT);
        const creditClientType = options.creditClientType;
        const creditCanceled = options.creditCanceled;

        const doc = new jsPDF({ orientation: 'portrait', putOnlyUsedFonts: true, });
        const pageStart = 10;
        const pageEnd = doc.internal.pageSize.getWidth() - 10;

        const drawReceiptCopy = (yOffset: number, copyType: 'estabelecimento' | 'cliente') => {
            let y = yOffset;

            const headerTitleX = 28;
            const headerValueX = 30;

            const bodyTitleX = 31;
            const bodyValueX = 32;

            const textAlign: TextOptionsLight = { align: 'right' };

            doc.setFontSize(10)
                .line(pageStart, y, pageEnd, y);
            y += 5;

            doc.setFont('helvetica', 'bold')
                .setFontSize(10)
                .text('Empresa:', headerTitleX, y, textAlign)
                .setFont('helvetica', 'normal')
                .text(this._companyData.name, headerValueX, y);

            if (!StringUtil.isEmpty(this._companyData.stateRegistration)) {
                doc.setFont('helvetica', 'bold')
                    .setFontSize(10)
                    .text('Inscrição Estadual:', pageEnd - doc.getTextWidth(`Inscrição Estadual: ${this._companyData.stateRegistration}`), y)
                    .setFont('helvetica', 'normal')
                    .setFontSize(10)
                    .text(this._companyData.stateRegistration, pageEnd - doc.getTextWidth(this._companyData.stateRegistration), y);
            }
            y += 6;

            doc.setFont('helvetica', 'bold')
                .setFontSize(10)
                .text('CNPJ:', headerTitleX, y, textAlign)
                .setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(this._companyData.cpfOrCnpj, headerValueX, y);
            y += 6;


            const addressText = `${this._companyData.street}, ${this._companyData.neighborhood}, ${this._companyData.city}`;
            doc.setFont('helvetica', 'bold')
                .setFontSize(10)
                .text('Endereço:', headerTitleX, y, textAlign)
                .setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(addressText, headerValueX, y);
            y += 6;

            doc.setFont('helvetica', 'bold')
                .setFontSize(10)
                .text('Telefone:', headerTitleX, y, textAlign)
                .setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(this._companyData.phones, headerValueX, y);

            const nowFormatted = moment().format('DD/MM/YYYY HH:mm:ss');
            doc.setFont('helvetica', 'bold')
                .setFontSize(10)
                .text('Data/Hora impressão:', pageEnd - doc.getTextWidth(`Data/Hora impressão: ${nowFormatted}`), y)
                .setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(nowFormatted, pageEnd - doc.getTextWidth('Data/Hora impressão:') + 2, y);
            y += 3;

            doc.line(pageStart, y, pageEnd, y);
            y += 10;

            const titleReceipt = ((creditClientType === 'CREDIT')
                ? creditCanceled ? 'Lançamento de crédito cancelado' : 'Crédito em haver'
                : creditCanceled ? 'Lançamento de débito cancelado' : 'Uso do crédito');

            doc.setFont('helvetica', 'bold')
                .setFontSize(14)
                .text(titleReceipt, (((pageEnd + 10) - doc.getTextWidth(titleReceipt)) / 2), y)
                .setFont('helvetica', 'normal')
                .setFontSize(8)
                .text(`Via ${copyType}`, pageEnd - doc.getTextWidth(`Via ${copyType}`), y);
            y += 10;

            doc.setFont('helvetica', 'bold')
                .setFontSize(10)
                .text('CPF/CNPJ:', bodyTitleX, y, textAlign)
                .setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(clientData.cpfOrCnpj, bodyValueX, y);
            y += 6;

            doc.setFont('helvetica', 'bold')
                .setFontSize(10)
                .text('Cliente:', bodyTitleX, y, textAlign)
                .setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(clientData.name, bodyValueX, y);
            y += 6;

            doc.setFont('helvetica', 'bold')
                .setFontSize(10)
                .text('Valor:', bodyTitleX, y, textAlign)
                .setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(creditClientAmountInWords, bodyValueX, y);
            y += 6;

            const descriptionSplitted = doc.splitTextToSize(creditClientDescription, pageEnd - 10 - doc.getTextWidth('Observação:'));
            doc.setFont('helvetica', 'bold')
                .setFontSize(10)
                .text('Observação:', bodyTitleX, y, { ...textAlign, lineHeightFactor: 1.55 })
                .setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(descriptionSplitted, bodyValueX, y);
            y += descriptionSplitted.length * 6;

            const messageAlert = ((creditClientType === 'CREDIT')
                ? (creditCanceled
                    ? 'O lançamento de crédito foi cancelado. Portanto, o valor informado acima não estará disponível.'
                    : 'Guarde bem este recibo. Ele será necessário para utilizar o valor informado acima.')
                : (creditCanceled
                    ? 'O lançamento de débito foi cancelado. Portanto, o valor informado acima voltará a ficar disponível.'
                    : 'O valor de crédito foi utilizado conforme informado acima. Guarde este recibo para controle.'
                )
            );

            doc.setFont('helvetica', 'bold')
                .setFontSize(10)
                .text('Atenção:', bodyTitleX, y, textAlign)
                .setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(messageAlert, bodyValueX, y)
            y += 12;

            const affirmationText = 'Por ser verdade, firmo o presente para que o mesmo sirva como documento.';
            doc
                .setFont('helvetica', 'normal')
                .setFontSize(11)
                .text(affirmationText, (((pageEnd + 10) - doc.getTextWidth(affirmationText)) / 2), y);
            y += 12;

            const localeAndDate = `${this._companyData.city}, ${creditClientDate}`;
            doc.setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(localeAndDate, (((pageEnd + 10) - doc.getTextWidth(localeAndDate)) / 2), y);
            y += 15;

            doc.line(pageStart + 40, y, pageEnd - 40, y);
            y += 8;

            const textName = creditClientType === 'CREDIT' ? this._companyData.name : clientData.name;
            doc.setFont('helvetica', 'normal')
                .setFontSize(10)
                .text(textName, (((pageEnd + 10) - doc.getTextWidth(textName)) / 2), y);
            y += 8;

            doc.line(pageStart, y, pageEnd, y);
            return y + 5;
        }

        let y = 10;

        // Desenha a via do estabelecimento
        y = drawReceiptCopy(y, 'estabelecimento');

        if (options.showTwoWays) {
            const cuttingLineY = y + 2;
            const cuttingText = 'Corte aqui';
            const cuttingTextWidth = doc.getTextWidth(cuttingText);
            const centerX = (pageStart + 10 + pageEnd) / 2;
            const textStart = centerX - (cuttingTextWidth / 2);

            doc.setLineDashPattern([1, 1], 0);

            // Parte esquerda da linha tracejada
            doc.line(pageStart, cuttingLineY, textStart - 2, cuttingLineY);

            // Texto centralizado
            doc.setFont('helvetica', 'normal')
                .setFontSize(8)
                .text(cuttingText, centerX - cuttingTextWidth / 2, cuttingLineY + 1);

            // Parte direita da linha tracejada
            doc.line(textStart + cuttingTextWidth, cuttingLineY, pageEnd, cuttingLineY);

            doc.setLineDashPattern([], 0);

            y += 10;

            // Desenha a via do cliente
            drawReceiptCopy(y, 'cliente');
        }

        /** Show pdf */
        const fileName: string = `recibo_credito_cliente_${moment().toString().replace('/', '_').replace(':', '_')}`
        doc.save(`${fileName}.pdf`);
    }

    private _getPersonInfo(person: FinancialPerson): PersonData {
        const address = !ArrayUtil.isEmpty(person.addressList)
            ? person.addressList.length > 0 ? person.addressList.find(e => e.useInInvoice) : person.addressList[0]
            : null;
        const phoneList = !ArrayUtil.isEmpty(person.phoneList)
            ? person.phoneList.slice(0, 3)
            : [];

        const name: string = StringUtil.isEmpty(person.fantasyName) ? person.name : person.fantasyName;

        let cpfOrCnpj: string = '';
        let stateRegistration: string = '';
        if (!ObjectUtil.isNull(person.legalPerson)) {
            cpfOrCnpj = !StringUtil.isEmpty(person.legalPerson.cnpj) ? StringUtil.format(person.legalPerson.cnpj, '00.000.000/0000-00') : '';

            const acronym: StateAcronym = (!ObjectUtil.isNull(address) && !ObjectUtil.isNull(address.city)) ? address.city.state.acronym as StateAcronym : null;
            stateRegistration = (!StringUtil.isEmpty(person.legalPerson.stateRegistration) && !StringUtil.isEmpty(acronym)) ? AcbrIEUtil.formatIE(acronym, person.legalPerson.stateRegistration) : '';
        } else if (!ObjectUtil.isNull(person.naturalPerson)) {
            cpfOrCnpj = !StringUtil.isEmpty(person.naturalPerson.cpf) ? StringUtil.format(person.naturalPerson.cpf, '000.000.000-00') : '';
            stateRegistration = '';
        }

        const street: string = !ObjectUtil.isNull(address) ? address.street : '';
        const acronym: StateAcronym = (!ObjectUtil.isNull(address) && !ObjectUtil.isNull(address.city)) ? address.city.state.acronym as StateAcronym : null;
        const city: string = !ObjectUtil.isNull(address) ? `${address.city.name}-${acronym}` : '';
        const zipCode: string = !ObjectUtil.isNull(address) ? StringUtil.format(address.zipCode, '00000-000') : '';
        const neighborhood: string = !ObjectUtil.isNull(address) ? address.neighborhood : '';
        const fones: string = !ArrayUtil.isEmpty(phoneList) ? phoneList.map(phone => PhoneUtil.format(phone.number)).join(', ') : '';

        return {
            name: name,
            cpfOrCnpj: cpfOrCnpj,
            stateRegistration: stateRegistration,
            street: street,
            city: city,
            zipCode: zipCode,
            neighborhood: neighborhood,
            phones: fones,
        }

    }

    private _getCompanyLogged(): void {
        try {
            const token: TokenDTO<OAuthUser> = this._authenticationService.token;
            if (this._authenticationService.isValidToken(token)) {
                this._companyService.getOne<FinancialCompany>(token.user.currentCompany).toPromise().then(company => {
                    if (!ObjectUtil.isNull(company)) {
                        this._companyData = this._getPersonInfo(company.person)
                    }
                });
            }
        } catch (e) {
            throw new Error(e);
        }
    }

}
